import {
  QUOTE_PLAN_TYPES,
  type QuotePlanParameters,
  type QuotePlanTemplate,
  type QuotePlanType,
} from "../types";

/**
 * Almikia Epic EQ (TASK-018): pure form rules of a payment plan template, kept
 * out of the React form so they can be unit tested. They mirror the
 * `quote_plan_templates` check constraints: every percentage between 0 and 100,
 * counts are non-negative integers, a monthly / deferred share needs at least one
 * payment, and the five percentages sum to 100.
 */

export const PLAN_PCT_FIELDS = [
  "down_payment_pct",
  "monthly_payments_pct",
  "deferred_payments_pct",
  "delivery_pct",
  "credit_pct",
] as const;

export const PLAN_COUNT_FIELDS = [
  "down_payment_count",
  "monthly_payments_count",
  "deferred_payments_count",
] as const;

export type PlanNumberField =
  | (typeof PLAN_PCT_FIELDS)[number]
  | (typeof PLAN_COUNT_FIELDS)[number]
  | "discount_pct";

/** Raw (string) values of the form; numbers are parsed by the rules below. */
export type PlanTemplateFormValues = {
  project_id: string;
  name: string;
  plan_type: QuotePlanType | "";
} & Record<PlanNumberField, string>;

/** Error code per field; the form translates `crm.quoter.templateForm.errors.<code>`. */
export type PlanTemplateFormErrors = Partial<
  Record<keyof PlanTemplateFormValues | "sum", string>
>;

export const EMPTY_PLAN_TEMPLATE_FORM: PlanTemplateFormValues = {
  project_id: "",
  name: "",
  plan_type: "",
  down_payment_pct: "",
  down_payment_count: "",
  monthly_payments_pct: "",
  monthly_payments_count: "",
  deferred_payments_pct: "",
  deferred_payments_count: "",
  delivery_pct: "",
  credit_pct: "",
  discount_pct: "",
};

const SUM_TOLERANCE = 1e-6;

/** Blank counts as 0; anything that is not a finite number is NaN. */
const parse = (raw: string): number => {
  const trimmed = raw.trim().replace(",", ".");
  return trimmed === "" ? 0 : Number(trimmed);
};

const isValidPct = (value: number) =>
  Number.isFinite(value) && value >= 0 && value <= 100;

const isValidCount = (value: number) => Number.isInteger(value) && value >= 0;

/** Sum of the five percentages; NaN when any of them is not a number. */
export const sumPlanPercentages = (values: PlanTemplateFormValues): number =>
  PLAN_PCT_FIELDS.reduce((sum, field) => sum + parse(values[field]), 0);

/**
 * Returns the error code of every invalid field (empty object = valid). The
 * `sum` key is reported only once every percentage is individually valid.
 */
export const validatePlanTemplateForm = (
  values: PlanTemplateFormValues,
): PlanTemplateFormErrors => {
  const errors: PlanTemplateFormErrors = {};
  if (values.project_id === "") errors.project_id = "project_required";
  if (values.name.trim() === "") errors.name = "name_required";
  if (!QUOTE_PLAN_TYPES.includes(values.plan_type as QuotePlanType)) {
    errors.plan_type = "plan_type_required";
  }
  for (const field of PLAN_PCT_FIELDS) {
    if (!isValidPct(parse(values[field]))) errors[field] = "pct_invalid";
  }
  for (const field of PLAN_COUNT_FIELDS) {
    if (!isValidCount(parse(values[field]))) errors[field] = "count_invalid";
  }
  if (
    values.discount_pct.trim() !== "" &&
    !isValidPct(parse(values.discount_pct))
  ) {
    errors.discount_pct = "pct_invalid";
  }
  if (!PLAN_PCT_FIELDS.some((field) => errors[field])) {
    if (Math.abs(sumPlanPercentages(values) - 100) > SUM_TOLERANCE) {
      errors.sum = "sum_not_100";
    }
  }
  if (
    !errors.monthly_payments_pct &&
    !errors.monthly_payments_count &&
    parse(values.monthly_payments_pct) > 0 &&
    parse(values.monthly_payments_count) === 0
  ) {
    errors.monthly_payments_count = "count_required";
  }
  if (
    !errors.deferred_payments_pct &&
    !errors.deferred_payments_count &&
    parse(values.deferred_payments_pct) > 0 &&
    parse(values.deferred_payments_count) === 0
  ) {
    errors.deferred_payments_count = "count_required";
  }
  return errors;
};

/** Parses valid form values into the numeric plan parameters (call only when valid). */
export const toPlanParameters = (
  values: PlanTemplateFormValues,
): QuotePlanParameters => ({
  down_payment_pct: parse(values.down_payment_pct),
  down_payment_count: parse(values.down_payment_count),
  monthly_payments_pct: parse(values.monthly_payments_pct),
  monthly_payments_count: parse(values.monthly_payments_count),
  deferred_payments_pct: parse(values.deferred_payments_pct),
  deferred_payments_count: parse(values.deferred_payments_count),
  delivery_pct: parse(values.delivery_pct),
  credit_pct: parse(values.credit_pct),
  discount_pct:
    values.discount_pct.trim() === "" ? null : parse(values.discount_pct),
});

/** Form values of an existing template (editing). */
export const planTemplateToFormValues = (
  template: QuotePlanTemplate,
): PlanTemplateFormValues => ({
  project_id: String(template.project_id),
  name: template.name,
  plan_type: template.plan_type,
  down_payment_pct: String(template.down_payment_pct),
  down_payment_count: String(template.down_payment_count),
  monthly_payments_pct: String(template.monthly_payments_pct),
  monthly_payments_count: String(template.monthly_payments_count),
  deferred_payments_pct: String(template.deferred_payments_pct),
  deferred_payments_count: String(template.deferred_payments_count),
  delivery_pct: String(template.delivery_pct),
  credit_pct: String(template.credit_pct),
  discount_pct:
    template.discount_pct == null ? "" : String(template.discount_pct),
});
